import { useCallback, useEffect, useRef, useState } from "react";
import {
  announceReady,
  onParentIntent,
  readIntentFromUrl,
  type EmbedIdentity,
  type EmbedIntent,
} from "../lib/embed";
import { usePublicCustomer, useUpdatePublicCustomer } from "./use-public-customer";

/** Two contacts are the same contact if they only differ by punctuation/case. */
const sameContact = (a?: string, b?: string) =>
  !!a &&
  !!b &&
  a.replace(/[\s.()-]/g, "").toLowerCase() ===
    b.replace(/[\s.()-]/g, "").toLowerCase();

/**
 * The same for phones, which the two sides rarely spell the same way: the host page
 * holds what its own form collected ("06 12 34 56 78") and the record holds what the
 * backend canonicalised ("+33612345678"). Comparing them literally asks the visitor
 * for a code they already gave, on every single page load. So the national significant
 * digits decide — the same trailing run the answer guard matches contacts on.
 */
const samePhone = (a?: string, b?: string) => {
  if (sameContact(a, b)) return true;
  const digits = (value?: string) => (value || "").replace(/\D/g, "");
  const [left, right] = [digits(a), digits(b)];
  if (left.length < 9 || right.length < 9) return false;
  return left.slice(-9) === right.slice(-9);
};

/** What the host claims, as one string — the key we apply an identity at most once per. */
const signature = (identity: EmbedIdentity | null | undefined) =>
  identity
    ? [
        identity.given_name,
        identity.family_name,
        identity.email,
        identity.phone,
      ].join("|")
    : "";

/** A first message the host wants said, and where it came from. */
export interface PendingMessage {
  /** Empty when the host asked for the calendar without any words. */
  text: string;
  /** Open the booking calendar rather than ask the agent (`HeyUpChat({ booking })`). */
  booking?: boolean;
  /**
   * It rode in on the iframe URL rather than being pushed live — so it belongs to the
   * conversation being opened, not to whatever thread happens to be resumed. See
   * `EmbedIntent.fromUrl`.
   */
  fromUrl: boolean;
}

export interface EmbedIntentState {
  /**
   * A phone the host handed over that nobody has verified → the login dialog opens on
   * it and sends the code. `null` when there is nothing to verify.
   */
  otpPhone: string | null;
  clearOtpPhone: () => void;
  /** Bumped every time the host asks for the voice leg (0 = never asked). */
  audioRequest: number;
  /** Bumped every time the host asks to hang up. */
  hangupRequest: number;
  /** A first message to say on the visitor's behalf. Null once it has been dealt with. */
  pendingMessage: PendingMessage | null;
  clearPendingMessage: () => void;
  /** Text to write into the input without sending it. Null once it has been picked up. */
  draft: string | null;
  clearDraft: () => void;
}

/**
 * The host page's side of the conversation: whatever `HeyUpChat({ … })` was called with,
 * and the `data-*` defaults `embed.js` bakes into the iframe URL.
 *
 * The two halves are deliberately asymmetric. **Audio** is just relayed — the caller
 * decides, and the chat starts the leg. **Identity** is not, because the host is not an
 * authority on who is sitting in front of it: what it sends is written through the same
 * public customer endpoint the visitor's own form posts to (`user_written`, refused on a
 * contact the transport layer already locked), and a phone is never taken as verified —
 * it is what arms the OTP (`verify: false` opts out of the text and leaves the number
 * merely recorded). The one exception is the contact the merchant *signed*, which
 * `/public/start` already resolved server-side; re-verifying that one would ask a visitor
 * to confirm a number their own logged-in session just proved.
 */
export function useEmbedIntent(publicId: string | undefined): EmbedIntentState {
  const { data: customer, isFetched } = usePublicCustomer(publicId || "");
  const update = useUpdatePublicCustomer(publicId || "");

  const [audioRequest, setAudioRequest] = useState(0);
  const [hangupRequest, setHangupRequest] = useState(0);
  const [identity, setIdentity] = useState<EmbedIdentity | null>(null);
  // Whether the pending identity's contact was vouched for by the connector HMAC.
  const [signed, setSigned] = useState(false);
  // The host opted out of the OTP: record the number, text nobody.
  const [verify, setVerify] = useState(true);
  const [otpPhone, setOtpPhone] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<PendingMessage | null>(
    null,
  );
  const [draft, setDraft] = useState<string | null>(null);
  const appliedRef = useRef<string>("");

  const apply = useCallback((intent: EmbedIntent) => {
    if (intent.audio === true) setAudioRequest((n) => n + 1);
    if (intent.audio === false) setHangupRequest((n) => n + 1);
    if (intent.message?.trim() || intent.booking) {
      setPendingMessage({
        text: intent.message?.trim() || "",
        booking: !!intent.booking,
        fromUrl: !!intent.fromUrl,
      });
    }
    if (intent.draft?.trim()) setDraft(intent.draft.trim());
    const next = intent.identity;
    if (next) {
      // Merge rather than replace: a host that identifies in two steps (name on load,
      // phone once the visitor logs in) should not lose the first half.
      setIdentity((current) => ({ ...(current || {}), ...next }));
      setSigned(!!intent.signed);
      setVerify(intent.verify !== false);
    }
  }, []);

  // The intent `embed.js` wrote into the iframe URL, read once per loaded widget.
  //
  // Deliberately NOT waiting for a conversation: there may not be one yet, and a
  // `{ message }` from the host is precisely what brings one into being. What waits for
  // a session is the identity below, which has a customer record to write onto.
  const urlReadRef = useRef(false);
  useEffect(() => {
    if (urlReadRef.current) return;
    urlReadRef.current = true;
    apply(readIntentFromUrl());
  }, [apply]);

  // …and the ones pushed later, on a panel that is already open. Announce readiness only
  // once the listener is attached: that message is what makes the host flush its queue.
  useEffect(() => {
    const unsubscribe = onParentIntent(apply);
    announceReady();
    return unsubscribe;
  }, [apply]);

  useEffect(() => {
    // `customer` is undefined for a session with no customer record at all (a visitor
    // whose browser refuses localStorage, so there was no device token to resolve one).
    // Nothing to prefill onto there — but a phone still gets its OTP, which is the one
    // path that creates and binds the customer by itself.
    if (!publicId || !identity || !isFetched) return;
    const key = signature(identity);
    if (appliedRef.current === key) return;
    appliedRef.current = key;

    if (customer) {
      // A contact the transport layer proved (the number they called from, a phone
      // already verified by OTP) belongs to the channel: the backend refuses to
      // overwrite it and would reject the whole patch with it, so drop those fields.
      const patch = {
        given_name:
          identity.given_name !== customer.given_name
            ? identity.given_name
            : undefined,
        family_name:
          identity.family_name !== customer.family_name
            ? identity.family_name
            : undefined,
        email:
          identity.email &&
          !sameContact(identity.email, customer.email) &&
          !customer.email_is_locked
            ? identity.email
            : undefined,
        phone:
          identity.phone &&
          !samePhone(identity.phone, customer.phone) &&
          !customer.phone_is_locked
            ? identity.phone
            : undefined,
      };
      if (Object.values(patch).some(Boolean)) {
        // Best effort: an identity the host got wrong must never break the chat.
        update.mutate(patch, {
          onError: (error) =>
            console.warn("[HeyUp] could not apply the host identity", error),
        });
      }
    }

    // The phone is the whole point of the OTP: the host's word is not proof, so the
    // visitor confirms it here — unless it is already proven (locked/verified on the
    // record), or it *is* the contact the merchant signed (no email signed instead).
    const alreadyProven =
      !!customer &&
      samePhone(identity.phone, customer.phone) &&
      (customer.phone_is_verified || customer.phone_is_locked);
    const isSignedContact = signed && !identity.email;
    if (verify && identity.phone && !alreadyProven && !isSignedContact) {
      setOtpPhone(identity.phone);
    }
  }, [publicId, identity, signed, verify, customer, isFetched, update]);

  const clearOtpPhone = useCallback(() => setOtpPhone(null), []);
  const clearPendingMessage = useCallback(() => setPendingMessage(null), []);
  const clearDraft = useCallback(() => setDraft(null), []);

  return {
    otpPhone,
    clearOtpPhone,
    audioRequest,
    hangupRequest,
    pendingMessage,
    clearPendingMessage,
    draft,
    clearDraft,
  };
}
